import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Clock, Home, Users, MessageSquare, User, Settings, LogOut, Shield, Crown, Zap, BarChart3, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ThemeToggle';
import UpgradeToCreatorButton from './UpgradeToCreatorButton';
import NotificationManager from './NotificationManager';
import { useTranslation } from 'react-i18next';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigation = [
    { name: t('dashboard.title'), href: '/app', icon: Home, testId: 'nav-dashboard' },
    { name: t('dashboard.browseUsers'), href: '/browse', icon: Users, testId: 'nav-browse' },
    { name: 'Courses', href: '/courses', icon: BookOpen, testId: 'nav-courses' },
    { name: t('groups.title'), href: '/groups', icon: Users, testId: 'nav-groups' },
    { name: t('dashboard.transactions'), href: '/transactions', icon: Clock, testId: 'nav-transactions' },
    { name: t('dashboard.messages'), href: '/messages', icon: MessageSquare, testId: 'nav-messages' },
    { name: t('analytics.title'), href: '/analytics', icon: BarChart3, testId: 'nav-analytics' },
    { name: t('dashboard.profile'), href: '/profile', icon: User, testId: 'nav-profile' },
    { name: 'Settings', href: '/settings', icon: Settings, testId: 'nav-settings' },
  ];

  if (user?.role === 'admin' || user?.role === 'moderator') {
    navigation.push({ name: t('admin.title'), href: '/admin', icon: Shield, testId: 'nav-admin' });
  }
  
  if (user?.creator_status) {
    navigation.push({ name: 'Creator', href: '/creator', icon: Crown, testId: 'nav-creator' });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 dark:from-gray-900 dark:to-gray-800">
      {/* Top Bar */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/app')}>
              <img src="/chronos-logo.png" alt="Chronos" className="h-10 w-10" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">Chronos</span>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 bg-gradient-to-r from-slate-100 to-slate-200 px-4 py-2 rounded-full border border-slate-300 shadow-sm">
                <Clock className="h-4 w-4 text-slate-600" />
                <span className="font-semibold text-slate-700">{user?.credits_balance} {t('dashboard.credits')}</span>
              </div>
              
              {user?.premium_status && (
                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  Premium
                </Badge>
              )}
              
              {user?.creator_status && (
                <Badge className="bg-purple-100 text-purple-700 border-purple-300 flex items-center gap-1">
                  <Zap className="h-3 w-3" />
                  Creator
                </Badge>
              )}
              
              <LanguageSelector />
              <ThemeToggle />
              <NotificationManager />
              <UpgradeToCreatorButton />
              
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-teal-100 text-teal-600">
                    {user?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">@{user?.username || user?.email?.split('@')[0]}</p>
                </div>
              </div>
              
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = window.location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  data-testid={item.testId}
                  onClick={() => navigate(item.href)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'text-teal-600 dark:text-teal-400 border-b-2 border-teal-600 dark:border-teal-400 bg-teal-50 dark:bg-gray-800'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.name}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Clock className="h-5 w-5 text-teal-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">© 2025 Chronos. {t('landing.footer')}</span>
              <button
                onClick={() => navigate('/faq')}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
              >
                {t('faq.title')}
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium"
              >
                {t('privacy.title')}
              </button>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('dashboard.trustLevel')}: <span className="font-medium text-teal-600 dark:text-teal-400">{user?.reputation?.trust_level}</span>
            </div>
          </div>
          <div className="text-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-400">
              © 2025 Arcana Crypto Ltd. All rights reserved. Company No. 16371124 — Registered in England and Wales.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Developed under Arcana Framework™. AI Assistant: Lilith (GPT-5 Core).
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Support: <a href="mailto:chronos.support@protonmail.com" className="text-teal-600 dark:text-teal-400 hover:underline">chronos.support@protonmail.com</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}